import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Package,
  Truck,
  Calculator,
  ShoppingCart,
  Box,
  MessageCircle,
  User,
  Check,
  Loader2,
  Calendar,
  Bell,
  RefreshCw,
  Settings,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface TrendyolIntegration {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  tooltip: string;
  tooltipEn: string;
  icon: typeof Package;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error' | 'syncing';
  lastSync?: Date;
}

interface TrendyolSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sellerId: string;
  onSave: (settings: TrendyolSettings) => void;
}

export interface TrendyolSettings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

const defaultIntegrations: TrendyolIntegration[] = [
  {
    id: 'orders',
    name: 'Sipariş Entegrasyonu',
    nameEn: 'Order Integration',
    description: 'Siparişleri otomatik olarak çek ve analiz et',
    descriptionEn: 'Automatically fetch and analyze orders',
    tooltip: 'Tüm sipariş statülerini (Bekleyen, Hazırlanıyor, Kargoda, Teslim Edildi, İptal) takip edin. Sipariş trendlerini, ortalama sepet tutarını ve en çok satan ürünleri analiz edin.',
    tooltipEn: 'Track all order statuses (Pending, Preparing, Shipped, Delivered, Cancelled). Analyze order trends, average basket value and best-selling products.',
    icon: ShoppingCart,
    enabled: true,
    status: 'active',
  },
  {
    id: 'products',
    name: 'Ürün Entegrasyonu',
    nameEn: 'Product Integration',
    description: 'Ürün listesi, stok ve fiyat takibi',
    descriptionEn: 'Product list, stock and price tracking',
    tooltip: 'Tüm ürünlerinizi, stok durumlarını ve fiyatlarını görüntüleyin. Kategori ve marka bazında ürün dağılımını analiz edin.',
    tooltipEn: 'View all your products, stock levels and prices. Analyze product distribution by category and brand.',
    icon: Box,
    enabled: true,
    status: 'active',
  },
  {
    id: 'buybox',
    name: 'Buybox Analizi',
    nameEn: 'Buybox Analysis',
    description: 'Ürünlerinizin buybox durumunu takip edin',
    descriptionEn: 'Track your products buybox status',
    tooltip: 'Birden fazla satıcının sattığı ürünlerde buybox sıralamanızı görün. Buybox fiyatını ve rakiplerinizi analiz edin.',
    tooltipEn: 'See your buybox ranking for products sold by multiple sellers. Analyze buybox price and your competitors.',
    icon: Package,
    enabled: true,
    status: 'active',
  },
  {
    id: 'returns',
    name: 'İade Entegrasyonu',
    nameEn: 'Returns Integration',
    description: 'İade taleplerini ve sebeplerini analiz et',
    descriptionEn: 'Analyze return requests and reasons',
    tooltip: 'İade taleplerini, iade sebeplerini ve süreç durumlarını takip edin. En çok iade edilen ürünleri ve iade nedenlerini analiz edin.',
    tooltipEn: 'Track return requests, reasons and process status. Analyze most returned products and return reasons.',
    icon: Truck,
    enabled: true,
    status: 'active',
  },
  {
    id: 'finance',
    name: 'Finans Entegrasyonu',
    nameEn: 'Finance Integration',
    description: 'Cari hesap ekstresi ve kargo faturaları',
    descriptionEn: 'Account statements and cargo invoices',
    tooltip: 'Cari hesap ekstrenizi, hakediş ödemelerinizi ve kargo fatura detaylarını görüntüleyin. Gelir-gider analizleri yapın.',
    tooltipEn: 'View your account statement, settlement payments and cargo invoice details. Perform income-expense analysis.',
    icon: Calculator,
    enabled: true,
    status: 'active',
  },
  {
    id: 'qa',
    name: 'Soru Cevap Entegrasyonu',
    nameEn: 'Q&A Integration',
    description: 'Müşteri sorularını takip et ve yanıtla',
    descriptionEn: 'Track and respond to customer questions',
    tooltip: 'Müşterilerin ürünleriniz hakkında sorduğu soruları görüntüleyin ve yanıtlayın. Yanıt sürelerinizi ve müşteri memnuniyetini takip edin.',
    tooltipEn: 'View and respond to customer questions about your products. Track your response times and customer satisfaction.',
    icon: MessageCircle,
    enabled: true,
    status: 'active',
  },
  {
    id: 'seller',
    name: 'Satıcı Bilgileri',
    nameEn: 'Seller Info',
    description: 'Mağaza bilgileri ve adres yönetimi',
    descriptionEn: 'Store info and address management',
    tooltip: 'Mağaza bilgilerinizi ve kayıtlı adreslerinizi görüntüleyin. Depo ve iade adreslerinizi yönetin.',
    tooltipEn: 'View your store information and registered addresses. Manage your warehouse and return addresses.',
    icon: User,
    enabled: true,
    status: 'active',
  },
];

const syncFrequencyOptions = [
  { value: 900, label: '15 Dakika', labelEn: '15 Minutes' },
  { value: 1800, label: '30 Dakika', labelEn: '30 Minutes' },
  { value: 3600, label: 'Saatlik', labelEn: 'Hourly' },
  { value: 21600, label: '6 Saatte Bir', labelEn: 'Every 6 Hours' },
  { value: 43200, label: '12 Saatte Bir', labelEn: 'Every 12 Hours' },
  { value: 86400, label: 'Günlük', labelEn: 'Daily' },
];

export function TrendyolSettingsModal({
  isOpen,
  onClose,
  sellerId,
  onSave,
}: TrendyolSettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const [integrations, setIntegrations] = useState<TrendyolIntegration[]>(defaultIntegrations);
  const [syncFrequency, setSyncFrequency] = useState(3600);
  const [notifications, setNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'integrations' | 'sync' | 'advanced'>('integrations');

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? {
              ...integration,
              enabled: !integration.enabled,
              status: !integration.enabled ? 'active' : 'inactive',
            }
          : integration
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);

    // Create settings object
    const settings: TrendyolSettings = {
      syncFrequency,
      notifications,
      integrations: integrations.reduce((acc, int) => {
        acc[int.id] = int.enabled;
        return acc;
      }, {} as Record<string, boolean>),
    };

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    onSave(settings);
    setIsSaving(false);
    onClose();
  };

  const enabledCount = integrations.filter(i => i.enabled).length;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border-default flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-default bg-gradient-to-r from-[#F27A1A]/10 to-transparent">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#F27A1A] flex items-center justify-center shadow-lg">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Trendyol {isEnglish ? 'Settings' : 'Ayarları'}
              </h2>
              <p className="text-sm text-text-secondary">
                Seller ID: {sellerId} • {enabledCount} {isEnglish ? 'active integrations' : 'aktif entegrasyon'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-default">
          {[
            { id: 'integrations', label: isEnglish ? 'Integrations' : 'Entegrasyonlar', icon: Box },
            { id: 'sync', label: isEnglish ? 'Sync Settings' : 'Senkronizasyon', icon: RefreshCw },
            { id: 'advanced', label: isEnglish ? 'Advanced' : 'Gelişmiş', icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-[#F27A1A] text-[#F27A1A]'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              )}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'integrations' && (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary mb-4">
                {isEnglish
                  ? 'Enable or disable integrations to control what data is synced with Trendyol.'
                  : 'Trendyol ile senkronize edilecek verileri kontrol etmek için entegrasyonları açın veya kapatın.'}
              </p>

              {integrations.map(integration => {
                const Icon = integration.icon;
                return (
                  <div
                    key={integration.id}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer',
                      integration.enabled
                        ? 'border-[#F27A1A]/30 bg-[#F27A1A]/5 hover:border-[#F27A1A]/50'
                        : 'border-border-default hover:border-border-hover bg-bg-elevated/50'
                    )}
                    onClick={() => toggleIntegration(integration.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
                          integration.enabled
                            ? 'bg-[#F27A1A]/20 text-[#F27A1A]'
                            : 'bg-bg-elevated text-text-tertiary'
                        )}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-text-primary">
                            {isEnglish ? integration.nameEn : integration.name}
                          </h4>
                          <div className="group relative">
                            <Info
                              size={14}
                              className="text-text-tertiary hover:text-[#F27A1A] cursor-help transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 rounded-lg bg-bg-surface border border-border-default shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                              <p className="text-xs text-text-secondary leading-relaxed">
                                {isEnglish ? integration.tooltipEn : integration.tooltip}
                              </p>
                              <div className="absolute left-3 bottom-0 translate-y-1/2 rotate-45 w-2 h-2 bg-bg-surface border-r border-b border-border-default"></div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          {isEnglish ? integration.descriptionEn : integration.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {integration.enabled && (
                        <span className="flex items-center gap-1 text-xs text-green-500">
                          <CheckCircle2 size={14} />
                          {isEnglish ? 'Active' : 'Aktif'}
                        </span>
                      )}
                      <div
                        className={cn(
                          'w-11 h-6 rounded-full relative transition-colors',
                          integration.enabled ? 'bg-[#F27A1A]' : 'bg-bg-elevated border border-border-default'
                        )}
                      >
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform shadow-sm',
                            integration.enabled ? 'translate-x-5' : 'translate-x-0.5'
                          )}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-6">
              {/* Sync Frequency */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Calendar size={16} className="text-[#F27A1A]" />
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sıklığı'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {syncFrequencyOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSyncFrequency(option.value)}
                      className={cn(
                        'px-3 py-2.5 rounded-lg text-sm font-medium transition-all border',
                        syncFrequency === option.value
                          ? 'border-[#F27A1A] bg-[#F27A1A]/10 text-[#F27A1A]'
                          : 'border-border-default text-text-secondary hover:border-[#F27A1A]/50'
                      )}
                    >
                      {isEnglish ? option.labelEn : option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifications */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Bell size={16} className="text-[#F27A1A]" />
                  {isEnglish ? 'Notifications' : 'Bildirimler'}
                </label>
                <div className="space-y-2">
                  <label
                    className="flex items-center justify-between p-3 rounded-lg border border-border-default cursor-pointer hover:border-[#F27A1A]/50 transition-all"
                    onClick={() => setNotifications(!notifications)}
                  >
                    <span className="text-sm text-text-secondary">
                      {isEnglish ? 'Notify me when sync completes' : 'Senkronizasyon tamamlandığında bildir'}
                    </span>
                    <div
                      className={cn(
                        'w-11 h-6 rounded-full relative cursor-pointer transition-colors',
                        notifications ? 'bg-[#F27A1A]' : 'bg-bg-elevated'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                          notifications ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Manual Sync Button */}
              <div className="pt-4 border-t border-border-default">
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#F27A1A] text-[#F27A1A] hover:bg-[#F27A1A]/10 transition-colors font-medium">
                  <RefreshCw size={18} />
                  {isEnglish ? 'Sync Now' : 'Şimdi Senkronize Et'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-4">
              {/* API Rate Limit Info */}
              <div className="p-4 rounded-xl bg-[#F27A1A]/5 border border-[#F27A1A]/20">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-[#F27A1A] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary text-sm">
                      {isEnglish ? 'API Rate Limit' : 'API İstek Limiti'}
                    </h4>
                    <p className="text-xs text-text-secondary mt-1">
                      {isEnglish
                        ? 'Maximum 50 requests per 10 seconds per endpoint. Exceeding this limit will result in a 429 error.'
                        : 'Aynı endpoint için 10 saniyede maksimum 50 istek. Bu limiti aşmak 429 hatasına neden olur.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="pt-4 border-t border-border-default">
                <h4 className="text-sm font-medium text-error mb-3">
                  {isEnglish ? 'Danger Zone' : 'Tehlikeli Bölge'}
                </h4>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-error text-error hover:bg-error/10 transition-colors font-medium">
                  {isEnglish ? 'Reset All Settings' : 'Tüm Ayarları Sıfırla'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border-default bg-bg-elevated/50">
          <p className="text-xs text-text-tertiary">
            {enabledCount} / {integrations.length} {isEnglish ? 'integrations enabled' : 'entegrasyon aktif'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              {isEnglish ? 'Cancel' : 'İptal'}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#F27A1A] text-white hover:bg-[#E06910] transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {isEnglish ? 'Save Settings' : 'Ayarları Kaydet'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
